import React, { useEffect, useRef, useState } from 'react';
import { Columns, Check } from 'lucide-react';
import type { ColumnDef } from '../hooks/useColumnVisibility';

interface ColumnVisibilityMenuProps {
  columns: ColumnDef[];
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
  onReset: () => void;
}

/**
 * "Columns" dropdown button that lets an HQ admin show/hide table columns.
 * Visibility state itself lives in the caller's `useColumnVisibility` hook —
 * this component only renders the toggle list and reports clicks.
 */
export default function ColumnVisibilityMenu({ columns, isVisible, onToggle, onReset }: ColumnVisibilityMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 px-4 py-2 rounded-xl text-sm font-medium transition-colors text-zinc-300"
      >
        <Columns className="w-5 h-5" />
        Columns
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-20 py-2">
          <div className="px-3 pb-2 mb-1 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Show columns</span>
            <button
              onClick={onReset}
              className="text-[10px] font-bold uppercase tracking-wide text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Reset
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {columns.map((col) => {
              const visible = isVisible(col.key);
              const disabled = !!col.required;
              return (
                <button
                  key={col.key}
                  onClick={() => onToggle(col.key)}
                  disabled={disabled}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors ${
                    disabled ? 'text-zinc-600 cursor-default' : 'text-zinc-300 hover:bg-zinc-800'
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      visible ? 'bg-yellow-400 border-yellow-400' : 'bg-zinc-950 border-zinc-700'
                    }`}
                  >
                    {visible && <Check className="w-3 h-3 text-zinc-950" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 truncate">{col.label}</span>
                  {disabled && <span className="text-[9px] font-bold uppercase text-zinc-600">Locked</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
